<template>
  <div ref="additionalWrap"
       :class="$style.wrap">
    <div ref="additional"
         :class="$style.additional"
         :style="mainOffset">
      <v-radio-group ref="options"
                     :value="checkedModifierKey"
                     :class="$style.additionalOptions"
                     class="no-message"
                     row
                     @change="$emit('input', $event)">

        <span :class="$style.itemPointer"
              :style="triangleOffset" />

        <v-radio v-for="(modifierKey, index) in currentItemModifiers"
                 :key="`${index}-${itemKey}-modifier`"
                 :label="modifiers[modifierKey].name"
                 :value="modifierKey"
                 :class="$style.customRadio"
                 class="customRadioBtn"/>

      </v-radio-group>
    </div>
  </div>
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import { Getter } from 'vuex-class';
import { CATEGORY_ID, MODIFIER_ID } from '@/config/constants';

/**
 * Width of aside menu on the screens wider than 1080px
 * Is used for calculation of leftOffset of AdditionalOption at such screens
 */
const ASIDE_MENU_WIDTH = 213;
const KIOSK_ASIDE_MENU_WIDTH = 260;
const KIOSK_RESOLUTION = 1080;
const MOBILE_RESOLUTION = 940;

/**
 * Provide option select for item
 *
 * Props:
 * @property {any} itemKey - key to select modifier from store.
 */
@Component({
  name:  'AdditionalOption',
  props: {
    itemKey:            {},
    additional:         {},
    checkedModifierKey: {},
  },
})
export default class AdditionalOption extends Vue {
  /**
   * Retrieves modifiers from store
   */
  @Getter modifiers;

  /**
   * Retrieves items from store
   */
  @Getter items;
  @Getter modifierByKey;

  mainOffset = {
    left: 'auto',
  };

  triangleOffset = {
    transform: 'translateX(0)',
  };

  /**
   * Provide item object from store based on passed `itemKey` prop.
   * @returns {{
   *   name: string,
   *   orderIndex: number,
   *   maxCount: number,
   *   price: string,
   *   modifiers: [any],
   * }}
   */
  get currentItem() {
    return this.items[this.itemKey];
  }

  /**
   * Provide current item filtered modifiers
   * @returns {[]} modifiers keys list
   */
  get currentItemModifiers() {
    return this.currentItem.modifiers.filter((modifierKey) => {
      if ([
        CATEGORY_ID.BURGER_PREMIUM_TOPPINGS,
        CATEGORY_ID.BOWL_PREMIUM_TOPPINGS,
        CATEGORY_ID.KIDS_BURGER_PREMIUM_TOPPINGS,
        CATEGORY_ID.FRIES_PREMIUM_TOPPINGS,
      ].includes(this.currentItem.categoryKey)) {
        return modifierKey !== MODIFIER_ID.ADD_EXTRA;
      }

      return modifierKey;
    }).sort(this.sortModifiers);
  }

  /**
   * Sort modifiers by order index.
   * @param a Modifier key
   * @param b Modifier key
   * @returns {number}
   */
  sortModifiers(a, b) {
    const modifierA = this.modifierByKey(a);
    const modifierB = this.modifierByKey(b);

    return modifierA.orderIndex - modifierB.orderIndex;
  }

  mounted() {
    const rect = this.$refs.additional.getBoundingClientRect();

    /* Wrapper item and its parameters */
    const wrapperPoint = this.$refs.additionalWrap.getBoundingClientRect();

    this.calcOffset(rect, wrapperPoint);
    this.$nextTick(() => {
      window.addEventListener('resize', () => {
        this.calcOffset(rect, wrapperPoint);
      });
    });
  }

  /**
   * Calc offset.
   */
  calcOffset(rect, wrapperPoint) {
    const documentWidth = document.documentElement.clientWidth;
    const rectLeft =   rect.left;
    const rectRight =  rect.right;
    const rectWidth = rect.width;

    /* Left offset of wrap item */
    const wrapperLeft = wrapperPoint.left;

    /**
     * Set width of aside menu
     * to move left offset of the additional menu
     * to the left at this value
     */
    let asideMenuWidth = 0;

    if (documentWidth > KIOSK_RESOLUTION) {
      asideMenuWidth = ASIDE_MENU_WIDTH;
    } else if (documentWidth > MOBILE_RESOLUTION) {
      asideMenuWidth = KIOSK_ASIDE_MENU_WIDTH;
    }


    const rightOffset = documentWidth - rectRight;
    const leftOffset = rectLeft;

    if (leftOffset < 0) {
      this.setMainOffset(Math.abs(leftOffset));
      this.setTriangleOffset(leftOffset);
    } else if (rightOffset < 0) {
      this.setMainOffset(rightOffset);
      this.setTriangleOffset(Math.abs(rightOffset));
    } else {
      this.setMainOffset(0);
      this.setTriangleOffset(0);
    }


    const halfOfRectWidth = rectWidth / 2;

    /**
     * Check if the aside menu overlays additional menu,
     * and if it is, replace additional menu to the right
     * of the aside menu
     */
    if (wrapperLeft - halfOfRectWidth - asideMenuWidth < 0) {
      const leftDiff = Math.abs(wrapperLeft - halfOfRectWidth - asideMenuWidth);

      this.setMainOffset(leftDiff);
      this.setTriangleOffset(-leftDiff);
    }
  }

  /**
   * Set main offset.
   * @param {Number} offset.
   */
  setMainOffset(offset) {
    this.mainOffset.left = `${offset}px`;
  }

  /**
   * Set triangle offset.
   * @param {Number} offset.
   */
  setTriangleOffset(offset) {
    this.triangleOffset.transform = `translateX(${offset}px)`;
  }
}
</script>

<style lang="scss" module>
  @import "@/style/variables.scss";
  @import "@/style/helpers.scss";

  .wrap {
    position: relative;
    height: 45px;
    width: 0;
  }

  .additional {
    position: absolute;
    top: 5px;
    transform: translateX(-50%);
    z-index: 50;
  }

  .additionalOptions {
    display: flex;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: stretch;
    margin: 0;
    z-index: 2;
    background-color: $green2;
    padding: 0;
    .itemPointer {
      position: absolute;
      width: 0;
      height: 0;
      top: -10px;
      left: 50%;
      margin-left: -10px;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-bottom: 10px solid $green2;
    }
  }
  .additional > .additionalOptions > div > div > div {
    flex-wrap: nowrap;
  }
  .additional .customRadio {
    padding: 3.5px;
    background-color: $green2;
    position: relative;
    cursor: pointer;
    user-select: none;
    margin-right: 0;
    input + div::before,
    input + div + i {
      content: none;
      display: none;
    }
    input + div {
      width: calc(100% - 7px);
      height: calc(100% - 7px);
      left: 0;
      top: 0;
      margin: 3.5px 0;
      border-radius: 2px;
    }
    & > div {
      position: absolute;
      width: 100%;
      height: 100%;
      margin-right: 0;
    }
    label {
      width: 100%;
      font-family: 'Gotham', sans-serif;
      font-size: 14px;
      font-weight: 700;
      line-height: 13px;
      color: $white;
      padding: 11px 16px;
      text-align: center;
      white-space: nowrap;
      border-radius: 2px;
    }
  }

  @media screen and (max-width: $kiosk-breakpoint-max) {
    .wrap {
      height: 70px;
    }

    .additional {
      top: 20px;
    }

    .additional .additionalOptions {
      border-radius: 10px;
      background-color: $green2;
      .itemPointer {
        top: -14px;
        margin-left: -20px;
        border-left: 20px solid transparent;
        border-right: 20px solid transparent;
        border-bottom: 16px solid $green2;
      }
      & > div {
        width: 100%;
      }
      & > div > div {
        margin-bottom: 0;
        min-height: 0;
      }
      & > div > div:first-child {
        margin-bottom: 0;
      }
    }
    .additional .customRadio {
      padding: 9px;
      background-color: transparent;
      width: 100%;
      flex: 1 1 0;
      input + div {
        width: calc(100% - 18px);
        height: calc(100% - 14px);
        border-radius: 10px;
      }
      label {
        font-size: 26px;
        line-height: 38px;
        padding: 12px 6px;
        border-radius: 10px;
      }
    }
    .additional .additionalOptions .customRadio label {
      display: block;
      text-align: center;
    }
  }

  @media screen and (max-width: $mobile-breakpoint-max) {
    .wrap {
      height: 40px;
    }

    .additional {
      top: 2px;
    }

    .additional .additionalOptions {
      border-radius: 2px;
      padding: 0;
      .itemPointer {
        top: -10px;
        margin-left: -10px;
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-bottom: 10px solid $green2;
      }
    }
    .additional .customRadio {
      padding: 3.5px;
      background-color: $green2;
      position: relative;
      cursor: pointer;
      user-select: none;
      margin-right: 0;
      margin-bottom: 0;
      input + div::before,
      input + div + i {
        content: none;
        display: none;
      }
      input + div {
        width: calc(100% - 7px);
        height: calc(100% - 7px);
        left: 0;
        top: 0;
        margin: 3.5px 0;
        border-radius: 2px;
      }
      & > div {
        position: absolute;
        width: 100%;
        height: 100%;
        margin-right: 0;
      }
      label {
        font-size: rem(14px);
        line-height: rem(10px);
        padding: 11px 16px;
        border-radius: 2px;
      }
    }
  }

  @media (max-width: 500px) {
    .additional .customRadio {
      label {
        font-size: rem(14px);
        padding: 10px 5px;
      }
    }
  }

  @media (max-width: 400px) {
    .additional .customRadio {
      label {
        font-size: rem(12px);
        padding: 10px 2px;
      }
    }
  }

</style>

/* Global for modify vuetify */
<style lang="scss">
  @import "@/style/variables.scss";
  .v-input--radio-group {
    &.no-message {
      .v-messages {
        display: none;
      }
      .v-input__slot {
        margin-bottom: 0;
      }
    }
  }

  .customRadioBtn.accent--text {
    label {
      background-color: $dark-green;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/AdditionalOption.vue